import { useEffect, useState } from "react";
import { api, ApiError, AppConfig } from "../api/client";
import { useToast } from "../ui/toast";
import ProductName from "../ProductName.jsx";
import { productKeyForPlan } from "../products";

const zl = (n: number) => n.toLocaleString("pl-PL", { maximumFractionDigits: 2 });

type PeriodKey = "m1" | "m3" | "m12";
const PERIODS: Array<{ key: PeriodKey; label: string }> = [
  { key: "m1", label: "1 miesiąc" },
  { key: "m3", label: "3 miesiące" },
  { key: "m12", label: "12 miesięcy" },
];

export function Pricing() {
  const toast = useToast();
  const [cfg, setCfg] = useState<AppConfig | null>(null);
  const [original, setOriginal] = useState<AppConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getConfig()
      .then((c) => { setCfg(c); setOriginal(c); })
      .catch((e: ApiError) => setError(e.status === 403 ? "Brak dostępu (owner-only)." : e.message));
  }, []);

  if (error) return <div className="card empty">{error}</div>;
  if (!cfg || !original) return <div className="card empty">Ładowanie…</div>;

  // Prices per period; a plan saved before periods existed starts from its yearly price.
  const pricesOf = (p: AppConfig["plans"][number]) => p.prices ?? { m12: p.netYear };
  const setPlanPrice = (id: "standard" | "governance", key: PeriodKey, net: number | undefined) =>
    setCfg({
      ...cfg,
      plans: cfg.plans.map((p) => {
        if (p.id !== id) return p;
        const prices = { ...pricesOf(p), [key]: net };
        if (key !== "m12" && !net) delete prices[key]; // empty = period not offered
        return { ...p, prices: { ...prices, m12: prices.m12 ?? 0 }, netYear: prices.m12 ?? 0 };
      }),
    });

  const tosChanged = cfg.tos.text !== original.tos.text || cfg.tos.url !== original.tos.url;
  const gross = (net: number) => Math.round(net * (1 + cfg.vatRate) * 100) / 100; // same rounding as the backend

  const save = async () => {
    setBusy(true);
    try {
      const saved = await api.saveConfig(cfg);
      setCfg(saved); setOriginal(saved);
      toast(`Zapisano (configVersion ${saved.configVersion}, tosVersion ${saved.tos.version})`);
    } catch (e) {
      toast((e as ApiError).message || "Nie udało się zapisać");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 22, alignItems: "start" }}>
      <div>
        <div className="card" style={{ padding: 22, marginBottom: 18 }}>
          <div className="section-title" style={{ marginBottom: 4 }}>Cennik</div>
          <div className="section-hint" style={{ marginBottom: 16 }}>
            Każdy produkt ma własne ceny — netto w PLN za cały okres licencji. Brutto liczone z VAT. Puste pole (1 lub 3 miesiące) = okres niedostępny w sklepie.
            Portal pokazuje nowe ceny w ciągu ok. 10 minut od zapisu.
          </div>
          {cfg.plans.map((p) => {
            const pr = pricesOf(p);
            return (
              <div key={p.id} style={{ marginBottom: 18 }}>
                <div className="row" style={{ marginBottom: 8, gap: 10, alignItems: "center" }}>
                  <ProductName product={productKeyForPlan(p.id)} size="sm" />
                  {p.id === "governance" ? <span className="faint" style={{ fontSize: 12 }}>— produkt jeszcze niedostępny w sklepie</span> : null}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
                  {PERIODS.map(({ key, label }) => (
                    <div key={key} className="field" style={{ margin: 0 }}>
                      <label className="label" style={{ fontSize: 12 }}>{label} — netto</label>
                      <input className="input" type="number" min={0} step={1} value={pr[key] ?? ""}
                        placeholder={key === "m12" ? "" : "niedostępny"}
                        onChange={(e) => setPlanPrice(p.id, key, e.target.value === "" ? undefined : Math.max(0, Number(e.target.value)))} />
                      <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
                        {pr[key] ? <>brutto: <b>{zl(gross(pr[key]!))} zł</b></> : "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="field" style={{ maxWidth: 180, marginTop: 4 }}>
            <label className="label">Stawka VAT</label>
            <input className="input" type="number" step={0.01} min={0} max={1} value={cfg.vatRate}
              onChange={(e) => setCfg({ ...cfg, vatRate: Number(e.target.value) })} />
            <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>np. 0.23 = 23%</div>
          </div>
        </div>

        <div className="card" style={{ padding: 22, marginBottom: 18 }}>
          <div className="section-title" style={{ marginBottom: 4 }}>Regulamin (Terms of Use)</div>
          <div className="section-hint" style={{ marginBottom: 16 }}>
            Zmiana treści/URL <b>podbije tos.version</b> — wymusi ponowną akceptację u wszystkich klientów przy następnym starcie.
          </div>
          <div className="field"><label className="label">URL regulaminu</label>
            <input className="input" value={cfg.tos.url} onChange={(e) => setCfg({ ...cfg, tos: { ...cfg.tos, url: e.target.value } })} /></div>
          <div className="field"><label className="label">Treść (markdown / tekst)</label>
            <textarea className="textarea" value={cfg.tos.text} onChange={(e) => setCfg({ ...cfg, tos: { ...cfg.tos, text: e.target.value } })} /></div>
          {tosChanged && <div className="badge st-expiring"><span className="dot dot-expiring" />tos.version zostanie podbita</div>}
        </div>

        <div className="card" style={{ padding: 22 }}>
          <div className="section-title" style={{ marginBottom: 16 }}>Kontakt dostawcy</div>
          <div className="grid-2">
            <div className="field"><label className="label">E-mail</label>
              <input className="input" value={cfg.support.email} onChange={(e) => setCfg({ ...cfg, support: { ...cfg.support, email: e.target.value } })} /></div>
            <div className="field"><label className="label">Telefon</label>
              <input className="input" value={cfg.support.phone} onChange={(e) => setCfg({ ...cfg, support: { ...cfg.support, phone: e.target.value } })} /></div>
          </div>
        </div>

        <div className="row" style={{ marginTop: 18, justifyContent: "flex-end" }}>
          <span className="faint" style={{ fontSize: 12 }}>configVersion {original.configVersion} · tosVersion {original.tos.version}</span>
          <div className="topbar-spacer" />
          <button className="btn primary" disabled={busy} onClick={save}>{busy ? "Zapisuję…" : "Zapisz zmiany"}</button>
        </div>
      </div>

      <div className="card" style={{ padding: 20, position: "sticky", top: 0 }}>
        <div className="section-title" style={{ marginBottom: 4 }}>Podgląd (jak widzi aplikacja)</div>
        <div className="section-hint" style={{ marginBottom: 14 }}>z /config</div>
        {cfg.plans.map((p) => {
          const pr = pricesOf(p);
          return (
            <div key={p.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
              <ProductName product={productKeyForPlan(p.id)} size="sm" />
              {PERIODS.filter(({ key }) => pr[key]).map(({ key, label }) => (
                <div key={key} className="row" style={{ justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
                  <span className="muted">{label}</span>
                  <span className="tabular">{zl(pr[key]!)} zł <span className="faint">netto · {zl(gross(pr[key]!))} brutto</span></span>
                </div>
              ))}
            </div>
          );
        })}
        <div className="faint" style={{ fontSize: 12, marginTop: 12 }}>
          VAT {Math.round(cfg.vatRate * 100)}% · {cfg.support.email} · {cfg.support.phone}
        </div>
      </div>
    </div>
  );
}
